import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import Webcam from "react-webcam";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useAPI } from "../../../contexts/APIContext";
import { showToast } from "../../Toast";
import logger from "../../../utils/logger";
import LoadingAnimation from "../LoadingAnimation";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const ProfilePictureStep = () => {
  const { nextStep, prevStep, setProfilePicture, setOriginalProfileImage, profilePicture, username } =
    useOnboarding();
  const { user } = useAuth();
  const { apiClient } = useAPI();

  const existingAvatar = profilePicture && !profilePicture.startsWith("default:") ? profilePicture : null;
  const [mode, setMode] = useState(existingAvatar ? "choose" : "choose");
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatedAvatar, setAnimatedAvatar] = useState(existingAvatar);
  const [portalTarget, setPortalTarget] = useState(null);
  const webcamRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById("onboarding-continue-slot");
      if (el) setPortalTarget(el);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const getInitial = () => {
    if (user?.username) return user.username[0].toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return "P";
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImage(reader.result);
        setMode("upload");
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".gif"] },
    maxFiles: 1,
    multiple: false,
  });

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0);
      const mirroredImage = canvas.toDataURL("image/jpeg");
      setCapturedImage(mirroredImage);
      setShowCamera(false);
      setMode("camera");
    };
    img.src = imageSrc;
  }, [webcamRef]);

  const compressImage = async (imageDataUrl, maxWidth = 1024, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      };
      img.src = imageDataUrl;
    });
  };

  const handleAnimate = async (imageDataUrl) => {
    setIsAnimating(true);
    try {
      const compressedBlob = await compressImage(imageDataUrl, 1024, 0.8);
      const file = new File([compressedBlob], "photo.jpg", { type: "image/jpeg" });
      setOriginalProfileImage(imageDataUrl);
      const animateResponse = await apiClient.animateProfilePicture(file);

      if (animateResponse.isSafetyBlock || animateResponse.error === "SAFETY_BLOCK") {
        showToast("This image isn't suitable for kids' stories. Please upload a child-friendly photo.", "error", 6000);
        return;
      }
      if (animateResponse.status) {
        const animatedImageUrl = animateResponse.data?.image_url || animateResponse.image_url;
        if (animatedImageUrl) {
          setAnimatedAvatar(animatedImageUrl);
          setProfilePicture(animatedImageUrl);
          showToast("Your avatar has been created!", "success");
        } else {
          showToast("Failed to create avatar", "error");
        }
      } else {
        showToast("Failed to create avatar", "error");
      }
    } catch (error) {
      logger.error("Error animating image:", error);
      showToast("Failed to create avatar. Please try again.", "error");
    } finally {
      setIsAnimating(false);
    }
  };

  const handleSkip = () => {
    setProfilePicture(`default:${getInitial()}`);
    nextStep();
  };

  const handleContinue = () => {
    if (animatedAvatar) nextStep();
  };

  const handleRedoAvatar = () => {
    setAnimatedAvatar(null);
    setUploadedImage(null);
    setCapturedImage(null);
    setProfilePicture(null);
    setOriginalProfileImage(null);
    setMode("choose");
  };

  // Speech bubble row (panda + brand white card)
  const SpeechRow = ({ eyebrow, title, subtitle }) => (
    <div className="flex items-start gap-4 sm:gap-5 mb-8 sm:mb-10 w-full">
      <div className="flex-shrink-0">
        <div className="relative w-24 h-24 sm:w-32 sm:h-32">
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,101,248,0.15)", filter: "blur(28px)", borderRadius: "50%", transform: "scale(1.4)" }} />
          <img
            src="/assets/pixley-panda-nobg.png"
            alt="Pixley Panda"
            className="relative w-full h-full object-contain z-10"
            onError={(e) => { e.target.src = "/assets/pixley-panda.png"; }}
            style={{ filter: "drop-shadow(0px 6px 12px rgba(11,26,51,0.18))" }}
          />
        </div>
      </div>
      <div className="flex-1 relative">
        <div
          className="absolute z-0"
          style={{
            left: -8, top: 28, width: 16, height: 16,
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRight: "none", borderTop: "none",
            transform: "rotate(45deg)",
          }}
        />
        <div
          className="relative z-10 px-6 py-5 sm:px-7 sm:py-6"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 22,
            boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
          }}
        >
          {eyebrow && (
            <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              {eyebrow}
            </span>
          )}
          <h1
            style={{
              ...FREDOKA,
              color: "#0B1A33",
              fontWeight: 700,
              fontSize: "clamp(1.4rem, 2.6vw, 1.9rem)",
              textTransform: "uppercase",
              letterSpacing: "-0.01em",
              lineHeight: 1.1,
              margin: eyebrow ? "6px 0 6px" : "0 0 6px",
            }}
          >
            {title}
          </h1>
          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>{subtitle}</p>
        </div>
      </div>
    </div>
  );

  // Continue button portaled to footer
  const makeContinueBtn = (onClick, disabled, label = "Continue") => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 15,
        border: "none",
        borderRadius: 999,
        padding: "12px 22px",
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {label}
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );

  const makeSecondaryBtn = (onClick, label) => (
    <button
      onClick={onClick}
      className="transition active:scale-[0.97]"
      style={{
        ...FREDOKA,
        background: "#FFFFFF",
        color: "#5A6B85",
        fontWeight: 700,
        fontSize: 14,
        border: "2px solid rgba(11,26,51,0.10)",
        borderRadius: 999,
        padding: "11px 20px",
        boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      <AnimatePresence mode="wait">
        {/* CHOOSE MODE */}
        {mode === "choose" && !animatedAvatar && (
          <motion.div key="choose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full flex flex-col items-center">
            <SpeechRow eyebrow="Avatar" title="Let's create your magic!" subtitle="Upload a photo and we'll bring your child's avatar to life." />

            {/* Choice cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 w-full mb-5">
              <div
                {...getRootProps()}
                className={`group cursor-pointer relative overflow-hidden transition-all duration-200 active:scale-[0.98]`}
                style={{
                  background: "#FFFFFF",
                  border: isDragActive ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 22,
                  boxShadow: isDragActive
                    ? "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.20)"
                    : "0px 3px 0px rgba(11,26,51,0.06)",
                }}
              >
                <input {...getInputProps()} />
                <div className="p-6 sm:p-7 flex flex-col gap-4 min-h-[200px] sm:min-h-[220px] justify-between">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: 52, height: 52,
                      borderRadius: 14,
                      background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                      color: "#FFFFFF",
                      boxShadow: "0px 3px 0px #073B8E",
                    }}
                  >
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 4px" }}>
                      Upload Photo
                    </h2>
                    <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>
                      {isDragActive ? "Drop it here!" : "Choose from your gallery"}
                    </p>
                  </div>
                </div>
              </div>

              <div
                onClick={() => { setShowCamera(true); setMode("camera"); }}
                className="group cursor-pointer relative overflow-hidden transition-all duration-200 active:scale-[0.98]"
                style={{
                  background: "#FFFFFF",
                  border: "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 22,
                  boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
                }}
              >
                <div className="p-6 sm:p-7 flex flex-col gap-4 min-h-[200px] sm:min-h-[220px] justify-between">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: 52, height: 52,
                      borderRadius: 14,
                      background: "linear-gradient(135deg, #00CAFF 0%, #0065F8 100%)",
                      color: "#FFFFFF",
                      boxShadow: "0px 3px 0px #073B8E",
                    }}
                  >
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <circle cx="12" cy="13" r="3" />
                    </svg>
                  </div>
                  <div>
                    <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 4px" }}>
                      Take Photo
                    </h2>
                    <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>
                      Strike a pose with your camera
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tip */}
            <div
              className="w-full flex items-start gap-3"
              style={{
                background: "linear-gradient(180deg, #E0F2FE 0%, #BAE6FD 100%)",
                border: "2px solid rgba(2,132,199,0.22)",
                borderRadius: 16,
                padding: "12px 14px",
                boxShadow: "0px 2px 0px rgba(2,132,199,0.12)",
              }}
            >
              <div
                className="flex-shrink-0 flex items-center justify-center"
                style={{ width: 28, height: 28, borderRadius: "50%", background: "#FFFFFF", color: "#0369A1", boxShadow: "0px 1px 0px rgba(11,26,51,0.06)" }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4M12 16h.01M12 22a10 10 0 110-20 10 10 0 010 20z" />
                </svg>
              </div>
              <div>
                <p style={{ ...FREDOKA, color: "#0369A1", fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", margin: 0 }}>
                  Pixley Tip
                </p>
                <p style={{ ...FREDOKA, color: "#0C4A6E", fontSize: 13, margin: "2px 0 0" }}>
                  Use a clear photo where the face is visible and the lighting is bright.
                </p>
              </div>
            </div>

            {portalTarget && ReactDOM.createPortal(
              makeSecondaryBtn(handleSkip, "Skip for now"),
              portalTarget
            )}
          </motion.div>
        )}

        {/* CAMERA */}
        {mode === "camera" && showCamera && (
          <motion.div key="camera" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full flex flex-col items-center">
            <SpeechRow eyebrow="Camera" title="Say cheese!" subtitle="Strike your best pose — we'll turn it into a magical avatar." />

            <div
              className="w-full overflow-hidden mb-6"
              style={{
                background: "#FFFFFF",
                border: "2px solid rgba(11,26,51,0.08)",
                borderRadius: 22,
                boxShadow: "0px 4px 0px rgba(11,26,51,0.08), 0px 12px 28px rgba(11,26,51,0.08)",
              }}
            >
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                className="w-full"
                style={{ transform: "scaleX(-1)", display: "block" }}
                videoConstraints={{ facingMode: "user" }}
              />
              <div className="flex gap-3 p-4">
                <button
                  onClick={() => { setShowCamera(false); setMode("choose"); }}
                  className="flex-1 transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#FFFFFF",
                    color: "#5A6B85",
                    fontWeight: 700,
                    fontSize: 14,
                    border: "2px solid rgba(11,26,51,0.10)",
                    borderRadius: 999,
                    padding: "11px 18px",
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCapture}
                  className="flex-1 transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 14,
                    border: "none",
                    borderRadius: 999,
                    padding: "12px 18px",
                    boxShadow: "0px 4px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.22)",
                    cursor: "pointer",
                  }}
                >
                  Capture
                </button>
              </div>
            </div>

            {portalTarget && ReactDOM.createPortal(<div />, portalTarget)}
          </motion.div>
        )}

        {/* LOADING */}
        {isAnimating && (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full max-w-2xl">
            <LoadingAnimation sourceImage={mode === "upload" ? uploadedImage : capturedImage} />
          </motion.div>
        )}

        {/* PREVIEW */}
        {(mode === "upload" || (mode === "camera" && capturedImage)) && !animatedAvatar && !isAnimating && (
          <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full flex flex-col items-center">
            <SpeechRow eyebrow="Preview" title="Looking good!" subtitle="Ready to transform this into a magical avatar?" />

            <div className="relative mx-auto w-48 h-48 sm:w-56 sm:h-56 mb-6">
              <div
                style={{
                  position: "absolute",
                  inset: -10,
                  background: "linear-gradient(135deg, rgba(0,101,248,0.40), rgba(0,202,255,0.30))",
                  borderRadius: "50%",
                  filter: "blur(20px)",
                  opacity: 0.6,
                }}
              />
              <div
                className="relative w-full h-full overflow-hidden"
                style={{
                  borderRadius: "50%",
                  border: "5px solid #FFFFFF",
                  boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 0px 0px 4px rgba(0,101,248,0.20), 0px 18px 36px rgba(11,26,51,0.15)",
                }}
              >
                <img src={mode === "upload" ? uploadedImage : capturedImage} alt="Preview" className="w-full h-full object-cover" />
              </div>
            </div>

            <button
              onClick={() => { setUploadedImage(null); setCapturedImage(null); setMode("choose"); }}
              className="mb-4 transition active:scale-[0.97]"
              style={{
                ...FREDOKA,
                background: "transparent",
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 13,
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
                textUnderlineOffset: 4,
              }}
            >
              Try another photo
            </button>

            {portalTarget && ReactDOM.createPortal(
              makeContinueBtn(
                () => handleAnimate(mode === "upload" ? uploadedImage : capturedImage),
                false,
                "Transform into Avatar"
              ),
              portalTarget
            )}
          </motion.div>
        )}

        {/* RESULT */}
        {animatedAvatar && (
          <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full flex flex-col items-center">
            <SpeechRow eyebrow="Ready" title="Your avatar is ready!" subtitle="You look absolutely magical!" />

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="relative mx-auto w-56 h-56 sm:w-64 sm:h-64 mb-6"
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1], opacity: [0.55, 0.85, 0.55] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  inset: -16,
                  background: "radial-gradient(circle at center, rgba(0,202,255,0.55) 0%, rgba(0,101,248,0.0) 70%)",
                  filter: "blur(12px)",
                  borderRadius: "50%",
                }}
              />
              <div
                className="relative w-full h-full overflow-hidden"
                style={{
                  borderRadius: "50%",
                  border: "5px solid #FFFFFF",
                  boxShadow: "0px 6px 0px #073B8E, 0px 0px 0px 4px rgba(0,101,248,0.30), 0px 0px 60px rgba(0,202,255,0.45)",
                  background: "#FFFFFF",
                }}
              >
                <img src={animatedAvatar} alt="Your Avatar" className="w-full h-full object-cover" />
              </div>
            </motion.div>

            <button
              onClick={handleRedoAvatar}
              className="mb-4 transition active:scale-[0.97]"
              style={{
                ...FREDOKA,
                background: "transparent",
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 13,
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
                textUnderlineOffset: 4,
              }}
            >
              Try another photo
            </button>

            {portalTarget && ReactDOM.createPortal(
              makeContinueBtn(handleContinue, false),
              portalTarget
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfilePictureStep;
